import {
  DeleteIcon,
  UpIcon,
  DownIcon,
  AddIcon,
  PillarIcon,
} from "../../icons/icons";
import BtnTooltip from "../../tooltips/btnTooltip";

const css = {
  /**
   * Tool's classes
   */

  mainSectionContainer: "entity-section-container",
  sectionTitleContainer: "entity-section-title-container",
  sectionTitle: "entity-section-title",
  sectionDescriptionContainer: "entity-description-container",
  sectionDescription: "entity-description",

  entitiesContainer: "entity-section-entities-container",
  entityItem: "entity-section-item",
  entityItemTitle: "entity-section-item-title",
  entityItemUpBtn: "entity-section-item-up-btn",
  entityItemDownBtn: "entity-section-item-down-btn",
  entityItemDeleteBtn: "entity-section-item-delete-btn",

  addEntityContainer: "entity-section-add-entity-container",
  addEntityBtn: "entity-section-add-entity-btn",

  blockBtn: "block-btn",
  blockBtnSmall: "small",
  blockBtnLarge: "large",
  largeIcon: "large-icon",
  xlIcon: "xl-icon",
  xxlIcon: "xxl-icon",
  clear: "clear",
  focusVisibleBtn: "focus-visible-btn",
  tuneWrapper: "editor-block-tune",
  tuneTitle: "editor-block-tune-title",
  tuneText: "editor-block-tune-text",
  tuneInput: "editor-block-tune-input",
  editorFont: "editor-font",
  editorBlockModal: "editor-block-modal",
  editorBlockModalTitle: "editor-block-modal-title",
  editorBlockModalLabel: "editor-block-modal-label",
  editorBlockModalText: "editor-block-modal-text",
  editorBlockModalInput: "editor-block-modal-input",
  adminFont: "admin-font",
};

export default class TrainingSection {
  static get toolbox() {
    return {
      icon: PillarIcon,
      title: "Training Section",
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;

    /**
     * Tool's settings passed from Editor
     *
     * @type {TitleConfig}
     * @private
     */
    this._settings = config;

    this._data = {
      title: data.title || "",
      description: data.description || "",
      entities: data.entities || [],
    };
  }

  getTitle() {
    const container = document.createElement("div");
    const tag = document.createElement("h6");

    container.classList.add(css.sectionTitleContainer);
    tag.classList.add(css.sectionTitle);

    if (!this.readOnly) {
      tag.setAttribute("contenteditable", "true");
    }

    tag.innerHTML = this._data.title;

    container.appendChild(tag);

    return container;
  }

  getDescription() {
    const container = document.createElement("div");
    const tag = document.createElement("p");

    container.classList.add(css.sectionDescriptionContainer);
    tag.classList.add(css.sectionDescription);

    if (!this.readOnly) {
      tag.setAttribute("contenteditable", "true");
    }

    tag.innerHTML = this._data.description;
    container.appendChild(tag);
    return container;
  }

  entityItem(item, index) {
    const container = document.createElement("div");
    const title = document.createElement("p");
    const upBtn = document.createElement("button");
    const downBtn = document.createElement("button");
    const deleteBtn = document.createElement("button");

    container.classList.add(css.entityItem);
    title.classList.add(css.entityItemTitle);
    upBtn.classList.add(css.entityItemUpBtn, css.blockBtn, css.blockBtnSmall);
    downBtn.classList.add(
      css.entityItemDownBtn,
      css.blockBtn,
      css.blockBtnSmall,
    );
    deleteBtn.classList.add(
      css.entityItemDeleteBtn,
      css.blockBtn,
      css.blockBtnSmall,
      css.clear,
    );

    title.innerHTML = item.title;
    upBtn.innerHTML = UpIcon;
    downBtn.innerHTML = DownIcon;
    deleteBtn.innerHTML = DeleteIcon;

    if (!this.readOnly) {
      upBtn.onclick = () => {
        if (index === 0) return;
        const entities = this._data.entities;
        [entities[index - 1], entities[index]] = [
          entities[index],
          entities[index - 1],
        ];
        this.refreshEntities();
        this.block.dispatchChange();
      };

      downBtn.onclick = () => {
        const entities = this._data.entities;
        if (index === entities.length - 1) return;
        [entities[index], entities[index + 1]] = [
          entities[index + 1],
          entities[index],
        ];
        this.refreshEntities();
        this.block.dispatchChange();
      };

      deleteBtn.onclick = () => {
        this._data.entities.splice(index, 1);
        this.refreshEntities();
        this.block.dispatchChange();
      };
    }

    container.appendChild(title);
    container.appendChild(upBtn);
    container.appendChild(downBtn);
    container.appendChild(deleteBtn);

    return container;
  }

  refreshEntities() {
    this._entitiesContainer.innerHTML = "";
    this._data.entities.forEach((item, index) => {
      this._entitiesContainer.appendChild(this.entityItem(item, index));
    });
  }

  getEntities() {
    this._entitiesContainer = document.createElement("div");
    this._entitiesContainer.classList.add(css.entitiesContainer);

    this._data.entities.forEach((item, index) => {
      this._entitiesContainer.appendChild(this.entityItem(item, index));
    });

    return this._entitiesContainer;
  }

  addEntities() {
    const container = document.createElement("div");
    const tag = document.createElement("button");

    container.classList.add(css.addEntityContainer);
    tag.classList.add(css.addEntityBtn, css.blockBtn);

    tag.innerHTML = `${AddIcon} Select resource`;

    tag.onclick = () => {
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      const block = this.api.blocks.getBlockByIndex(blockIndex);

      const data = {
        title: this.title.querySelector(`.${css.sectionTitle}`).innerHTML,
        description: this.description.querySelector(
          `.${css.sectionDescription}`,
        ).innerHTML,
        entities: this._data.entities,
      };
      this._settings.showEntitySelector({
        details: block,
        data: data,
      });
    };

    container.appendChild(tag);
    return container;
  }

  render() {
    const container = document.createElement("div");
    container.classList.add(css.mainSectionContainer);

    this.title = this.getTitle();
    this.description = this.getDescription();

    container.appendChild(this.title);
    container.appendChild(this.description);
    container.appendChild(this.getEntities());
    container.appendChild(this.addEntities());

    return container;
  }

  save() {
    const data = {
      title: this.title.querySelector(`.${css.sectionTitle}`).innerHTML,
      description: this.description.querySelector(`.${css.sectionDescription}`)
        .innerHTML,
      entities: this._data.entities,
    };
    return data;
  }
}
